import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import reportWebVitals from "./reportWebVitals";
import { ReactKeycloakProvider } from "@react-keycloak/web";
import App from "./App/App";
import keycloak from "./common/services/auth/keycloak";
import httpService from "./common/services/app/httpService";
import "./index.css";
import "./common/components/d3/d3-charts.css";
import { toast } from "react-toastify";
import "./common/services/app/i18n";
import * as Sentry from "@sentry/react";

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  integrations: [
    // send console.log, console.warn, and console.error calls as logs to Sentry
    Sentry.consoleLoggingIntegration({ levels: [/*"log",*/ "warn", "error"] }), //Exclude log - too verbose
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(), // 👈 optional, see section 4
  ],
  // Enable logs to be sent to Sentry
  enableLogs: true,
  environment: process.env.REACT_APP_ENVIRONMENT,
  release: process.env.REACT_APP_VERSION,
  autoSessionTracking: true,
  tracesSampleRate: 0.2, // 👈 see section 3 for tuning
  replaysSessionSampleRate: 0.05, // record 5% of sessions
  replaysOnErrorSampleRate: 1.0, // record all sessions with an error
  ignoreErrors: ["ResizeObserver loop limit exceeded", "Network Error"],
  denyUrls: [/extensions\//i, /^chrome:\/\//i],
});

Sentry.setTags({
  app_version: process.env.REACT_APP_VERSION,
  //build_commit: process.env.REACT_APP_COMMIT_HASH,
  //region: "us-west", // or whatever makes sense for your infra
});

const handleEvent = async (event, error) => {
  console.log("index - onKeycloakEvent", event, error);
  if (event === "onAuthSuccess" || event === "onAuthRefreshSuccess") {
    httpService.setJwt(keycloak.token);
    if (event === "onAuthSuccess") {
      Sentry.setUser({
        id: keycloak.tokenParsed?.OriginatingUserAccountID,
        email: keycloak.tokenParsed?.email,
        username: keycloak.tokenParsed?.preferred_username,
      });
      Sentry.captureMessage(`Login`);
    }
  } else if (event === "onAuthLogout" || event === "onAuthRefreshError") {
    if (event === "onAuthRefreshError") {
      Sentry.captureMessage(`Session Expired`);
    }
    //window.location ='/';
    keycloak.login();
    toast("Your session has been logged out.", { autoClose: false });
  } else if (event === "onInitError") {
    console.warn("Keycloak init failed. Retrying in 60s...");
    if (window.location.pathname !== "/maintenance") {
      window.location = "/maintenance";
      toast.warning("Unable to connect to authentication service.", {
        autoClose: false,
      });
    }
    setTimeout(() => {
      window.location.reload(); // retry cleanly
    }, 60000);
  } else if (event === "onReady") {
    if (window.location.pathname === "/maintenance") {
      window.location = "/";
    }
  }
};

const handleTokens = (tokens) => {
  console.log("index - onKeycloakTokens");
  httpService.setJwt(tokens.token);
};

const handleLogout = () => {
  Sentry.captureMessage("Logout");

  keycloak.logout({
    redirectUri: window.location.origin, // or wherever you want
  });
};

const root = ReactDOM.createRoot(document.getElementById("root"));

root.render(
  <ReactKeycloakProvider
    authClient={keycloak}
    initOptions={{
      onLoad: "login-required",
      promiseType: "native",
      checkLoginIframe: false,
      silentCheckSsoRedirectUri:
        window.location.origin + "/silent-check-sso.html",
    }}
    autoRefreshToken={true}
    onEvent={handleEvent}
    onTokens={handleTokens}
  >
    <React.StrictMode>
      <BrowserRouter>
        <App handleLogout={handleLogout} />
      </BrowserRouter>
    </React.StrictMode>
  </ReactKeycloakProvider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
